import ThumbnailWithFallback from '~/components/photos/thumbnail-with-fallback';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '~/components/ui/accordion';
import { Button } from '~/components/ui/button';
import { Icon } from '~/components/ui/icon';
import { type AthleteGymDetails } from '~/domain/gyms/models/gym';
import { THUMBNAIL_SIZE } from '~/domain/shared/models/thumbnail-size';
import { cn } from '~/styles/extended-theme.ts';
import { displayName } from '~/utils/display-name';
import { type PropsWithClassName } from '~/utils/props-with-classname';

export function StaffSection({ staff }: { staff: AthleteGymDetails['staff'] }) {
  return (
    <Accordion type="single" collapsible>
      {staff.map((s) => (
        <AccordionItem key={s.id.toString()} value={s.id.toString()} className="border-b-0">
          <StaffCardAccordion staff={s} key={s.id} className="mb-4" />
        </AccordionItem>
      ))}
    </Accordion>
  );
}

interface Props {
  staff: AthleteGymDetails['staff'][0];
}

export function StaffCardAccordion({ staff, className }: PropsWithClassName<Props>) {
  const rank = staff.belt
    ? `${staff.belt} Belt ${staff.degree ? ` — ${staff.degree} Degree` : ''}`
    : null;

  const hasNoDetails =
    !staff.phoneNumber && !staff.email && !rank && !staff.about && !staff.teacherAndGym;

  return (
    <>
      <AccordionTrigger
        disabled={hasNoDetails}
        className={cn('text-left hover:no-underline', {
          '[&>svg]:hidden': hasNoDetails,
        })}
      >
        <StaffCard className={className} staff={staff} />
      </AccordionTrigger>

      <AccordionContent className="mb-8 data-[state=closed]:animate-slide-up data-[state=open]:animate-slide-down">
        <div className="flex flex-wrap gap-2 tablet:gap-4">
          {staff.phoneNumber ? (
            <Button asChild variant="outline" className="w-24">
              <a href={`tel:${staff.phoneNumber}`}>Call</a>
            </Button>
          ) : null}

          {staff.email ? (
            <Button asChild variant="outline" className="w-24">
              <a href={`mailto:${staff.email}`}>Email</a>
            </Button>
          ) : null}
        </div>

        {rank ? (
          <div className="mt-4 flex items-center gap-2 text-body2 font-light">
            <Icon name="rank" size="lg" /> {rank}
          </div>
        ) : null}

        {staff.about ? (
          <>
            <h2 className="mt-6 font-sans text-subtitle1">About</h2>
            <p className="mt-2 whitespace-pre-wrap text-body2">{staff.about}</p>
            <hr className="my-4" />
          </>
        ) : null}

        {staff.teacherAndGym ? (
          <>
            <h2 className="mt-6 font-sans text-subtitle1">Lineage</h2>
            <p className="mt-2 whitespace-pre-wrap text-body2">{staff.teacherAndGym}</p>
          </>
        ) : null}
      </AccordionContent>
    </>
  );
}

export function StaffCard({ staff, className }: PropsWithClassName<Props>) {
  const name = displayName(staff.firstName, staff.lastName);

  return (
    <div className={cn('flex gap-4', className)}>
      <div className="row-span-3 h-[80px] w-[80px] shrink-0 overflow-hidden rounded-2xl">
        <ThumbnailWithFallback
          url={staff.photoUrl ?? ''}
          variant="person"
          alt={name}
          size={THUMBNAIL_SIZE.SQUARE_EXTRA_SMALL}
        />
      </div>

      <div className="flex flex-grow flex-col gap-1">
        {staff.titles.length > 0 ? (
          <p className="text-subtitle2 text-secondary">{staff.titles.join(', ')}</p>
        ) : null}

        <p className="text-subtitle2">{name}</p>

        {staff.expertises.length > 0 ? (
          <div className="mt-1 flex flex-wrap gap-2">
            {staff.expertises.map((e) => (
              <div key={e} className="rounded-lg bg-gray-200 px-2 py-1 text-body2 font-light">
                {e}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
